// Extracted from Dashboard/index.web.jsx on 2026-10-02.
import { useCallback, useEffect, useState } from 'react';
import { usePerms } from 'contexts/PermsContext';
import GetWidgetData from 'data/widgetData/widgetData';
import { useGetRoleQuery } from 'services/roles/roles.api';
import {
  useGetUserWidgetDataQuery,
  useUpdateUserWidgetLayoutMutation,
} from 'services/user/widgets/widgets.api';
import { safeJSONParse } from 'utils/data/parse.utils';
import dashboardConfig from '../config/dashboard.config';
import type { DashboardLayoutOptions, GridItem } from '../types/dashboard.types';
import type { LayoutItem, WidgetData } from '../types/widgetTypes';
import { findNextGridPlacement } from '../utils/dashboardLayout.utils';
import {
  isMapWidgetDefinition,
  isChartWidgetDefinition,
  isConnectedDevicesListWidgetDefinition,
  getOverwatchWidgetResizeProfile,
} from '../utils/widgetSizing.utils';

const DASHBOARD_COLS_BY_BREAKPOINT = dashboardConfig['columns-by-breakpoint'];
const useDashboardLayout = ({
  definedWidgetData,
  breakpoint: bp,
  canOverrideLockedFromRole,
}: DashboardLayoutOptions) => {
  const perms = usePerms();
  const [list, setList] = useState<LayoutItem[]>([]);
  const [choiceList, setChoiceList] = useState<LayoutItem[] | null>(null);
  const { data: userWidgets, isFetching: userWidgetsFetching } =
    useGetUserWidgetDataQuery(undefined);
  const [updateUserWidgetLayout] = useUpdateUserWidgetLayoutMutation();
  const canViewRoles = perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewRoles']);
  const userRole = perms.roleId !== null ? parseInt(String(perms.roleId), 10) : -1;
  const { data: roleData } = useGetRoleQuery(userRole, { skip: !canViewRoles || userRole <= -1 });

  useEffect(() => {
    // Wait for perms from context to load before trying to load widgets
    if (perms.isFetching || perms.data === null) return;

    const userRole = perms.roleId !== null ? parseInt(String(perms.roleId), 10) : -1;
    const widgetDefinitions: WidgetData = GetWidgetData();

    if (userRole > -1) {
      const filteredWidgetList = Object.entries(widgetDefinitions)
        .filter(([widgetId, widgetDefinition]) =>
          perms.testPermission([8, 9, 10, 11, 12, 13], widgetDefinition.permissions),
        )
        .map(([widgetId, widgetDefinition]) => ({
          // provide a consistent shape for choiceList entries
          ...widgetDefinition,
          display: true,
          w: widgetDefinition.w ?? 1,
          h: widgetDefinition.h ?? 1,
          isDraggable:
            widgetDefinition.isDraggable !== undefined ? widgetDefinition.isDraggable : true,
          locked: widgetDefinition.locked !== undefined ? widgetDefinition.locked : false,
          x: widgetDefinition.x ?? 0,
          y: widgetDefinition.y ?? 0,
          id: widgetId,
        }));

      setChoiceList(filteredWidgetList);
    } else {
      setChoiceList(
        Object.keys(widgetDefinitions).map((widgetId) => ({
          display: true,
          w: 1,
          h: 1,
          isDraggable: true,
          locked: false,
          x: 0,
          y: 0,
          id: widgetId,
        })),
      );
    }

    const loadWidgets = async () => {
      // Try role widgets first, otherwise fall back to user widgets
      if (userRole > -1) {
        try {
          // block is needed to ensure we don't try to load role widgets before we have the necessary permissions info from perms context
          if (canViewRoles && roleData) {
            const raw = roleData?.widget_list;
            let parsed: LayoutItem[] = [];
            if (raw !== undefined && raw !== null) {
              if (typeof raw === 'string') {
                const s = raw.trim();
                const p = safeJSONParse(s, []);
                parsed = Array.isArray(p) ? p : [];
              } else if (Array.isArray(raw)) {
                parsed = raw;
              } else if (typeof raw === 'object') {
                // sometimes API may wrap layout in an object
                if (Array.isArray(raw.layout)) parsed = raw.layout;
                else parsed = [];
              } else {
                parsed = [];
              }
            }

            if (parsed && parsed.length > 0) {
              const normalized = parsed.map((it) => {
                const locked = it.locked !== undefined ? !!it.locked : it.isDraggable === false;
                return {
                  ...it,
                  locked,
                  lockedFromRole: locked === true,
                  isDraggable: it.isDraggable !== undefined ? it.isDraggable : !locked,
                };
              });

              setList([...normalized]);
              return;
            }
          }
        } catch (e) {
          console.error('failed to load role widgets', e);
        }
      }

      // fallback to user widgets
      if (userWidgetsFetching) {
        return;
      }

      if (!userWidgets || userWidgets.length === 0) {
        console.debug('Dashboard: no role layout and userWidgets empty — setting empty list');
        setList([]);
        return;
      }

      // Ensure userWidgets is an array; API may return a JSON string or wrapped object.
      let finalUserWidgets = userWidgets;
      if (typeof finalUserWidgets === 'string') {
        finalUserWidgets = safeJSONParse(finalUserWidgets.trim(), []);
      }

      if (
        finalUserWidgets &&
        !Array.isArray(finalUserWidgets) &&
        typeof finalUserWidgets === 'object' &&
        Array.isArray(finalUserWidgets.layout)
      ) {
        finalUserWidgets = finalUserWidgets.layout;
      }

      if (!finalUserWidgets || finalUserWidgets.length === 0) {
        setList([]);
        return;
      }

      setList([...finalUserWidgets]);
    };

    loadWidgets().catch((e) => console.error(e));
  }, [
    userWidgets,
    userWidgetsFetching,
    perms.data,
    perms.roleId,
    perms.isFetching,
    roleData,
    canViewRoles,
  ]);

  // Stable identity so the memoized widget list below is not invalidated on
  // every Dashboard render (e.g. alarm/alert polling).
  const onListChange = useCallback(
    async (newList: LayoutItem[]) => {
      setList(newList);

      //update user settings
      newList = newList.map((item) => {
        if (!item.w) item = { ...item, w: 1 };
        if (!item.h) item = { ...item, h: 1 };
        if (item.id !== undefined) item = { ...item, id: parseInt(String(item.id), 10) };
        return item;
      });

      try {
        await updateUserWidgetLayout(newList).unwrap();
      } catch (error) {
        console.error('Failed to update user widget layout');
      }
    },
    [updateUserWidgetLayout],
  );

  const persistGridLayoutChanges = (gridLayout: GridItem[], { includeSize = false } = {}) => {
    const temp = [...list];
    const maxGridWidth = DASHBOARD_COLS_BY_BREAKPOINT.xxl;
    const activeCols = DASHBOARD_COLS_BY_BREAKPOINT[bp] ?? DASHBOARD_COLS_BY_BREAKPOINT.xxl;

    // The saved layout is the desktop arrangement. Single-column (mobile)
    // breakpoints force every widget to x=0/w=1, so nothing done there counts
    // as a user rearrangement — persisting it would collapse the full-screen
    // layout into one column. Mobile reordering stays visual-only and the
    // desktop positions are restored untouched when the viewport grows back.
    if (activeCols <= 1) return;

    for (let i = 0; i < gridLayout.length; i++) {
      const gridItem = gridLayout[i];
      const id = gridItem.i;
      if (id === 'add') continue;

      const idx = parseInt(id, 10);
      if (Number.isNaN(idx) || !temp[idx]) continue;

      const currentItem = temp[idx];
      const widgetDefinition = definedWidgetData?.[currentItem?.id];
      const mapResizable = isMapWidgetDefinition(widgetDefinition);
      const chartResizable = isChartWidgetDefinition(widgetDefinition);
      const overwatchResizeProfile = getOverwatchWidgetResizeProfile(widgetDefinition, activeCols);
      const connectedDevicesListResizable =
        isConnectedDevicesListWidgetDefinition(widgetDefinition);
      const maxChartWidth = Math.min(4, activeCols);
      const minChartWidth = Math.min(2, activeCols);
      const maxConnectedDevicesListWidth = Math.min(2, activeCols);

      let nextW = includeSize
        ? mapResizable
          ? Math.max(1, Math.min(activeCols || maxGridWidth, Number(gridItem.w) || 1))
          : overwatchResizeProfile
            ? Math.max(
                overwatchResizeProfile.minW,
                Math.min(overwatchResizeProfile.maxW, Number(gridItem.w) || 1),
              )
            : connectedDevicesListResizable
              ? Math.max(1, Math.min(maxConnectedDevicesListWidth, Number(gridItem.w) || 1))
              : chartResizable
                ? Math.max(minChartWidth, Math.min(maxChartWidth, Number(gridItem.w) || 1))
                : (currentItem.w ?? 1)
        : (currentItem.w ?? 1);

      let nextH = includeSize
        ? mapResizable
          ? Math.max(1, Math.min(2, Number(gridItem.h) || 1))
          : overwatchResizeProfile
            ? Math.max(
                overwatchResizeProfile.minH,
                Math.min(overwatchResizeProfile.maxH, Number(gridItem.h) || 1),
              )
            : connectedDevicesListResizable
              ? 2
              : chartResizable
                ? Math.max(1, Math.min(2, Number(gridItem.h) || 1))
                : (currentItem.h ?? 1)
        : (currentItem.h ?? 1);

      if (includeSize && overwatchResizeProfile?.disallowOneByOne && nextW === 1 && nextH === 1) {
        nextH = 2;
      }

      temp[idx] = {
        ...currentItem,
        x: Number.isFinite(gridItem.x) ? gridItem.x : (currentItem.x ?? 0),
        y: Number.isFinite(gridItem.y) ? gridItem.y : (currentItem.y ?? 0),
        ...(includeSize
          ? {
              w: nextW,
              h: nextH,
            }
          : {}),
      };
    }

    onListChange(temp);
  };

  const addWidgets = (widgets: LayoutItem[]) => {
    if (!Array.isArray(widgets) || !widgets.length) return;

    // Saved positions describe the desktop grid, so placement is always
    // computed against a multi-column grid — adding a widget from the
    // single-column mobile view must not stack it into desktop column 0.
    const breakpointCols = DASHBOARD_COLS_BY_BREAKPOINT[bp] ?? DASHBOARD_COLS_BY_BREAKPOINT.xxl;
    const activeCols = breakpointCols <= 1 ? DASHBOARD_COLS_BY_BREAKPOINT.xxl : breakpointCols;

    const placedWidgets = list.map((item) => ({
      x: Number.isFinite(item?.x) ? item.x : 0,
      y: Number.isFinite(item?.y) ? item.y : 0,
      w: Math.max(1, Math.min(activeCols, Number(item?.w) || 1)),
      h: Math.max(1, Number(item?.h) || 1),
    }));

    const normalizedIncomingWidgets = widgets.map((widget) => {
      const w = Math.max(1, Math.min(activeCols, Number(widget?.w) || 1));
      const h = Math.max(1, Number(widget?.h) || 1);
      const { x, y } = findNextGridPlacement(placedWidgets, activeCols, w, h);

      const normalizedWidget = { ...widget, w, h, x, y };
      placedWidgets.push({ x, y, w, h });
      return normalizedWidget;
    });

    return onListChange([...list, ...normalizedIncomingWidgets]);
  };

  const removeWidget = useCallback(
    (toRemove: number) => {
      onListChange(list.filter((_, index) => index !== toRemove));
    },
    [list, onListChange],
  );

  const removeAllWidgets = () => {
    if (!Array.isArray(list) || list.length === 0) {
      return;
    }

    if (canOverrideLockedFromRole) {
      onListChange([]);
      return;
    }

    onListChange(list.filter((item) => item?.lockedFromRole === true));
  };

  return {
    list,
    choiceList,
    onListChange,
    persistGridLayoutChanges,
    addWidgets,
    removeWidget,
    removeAllWidgets,
  };
};

export default useDashboardLayout;
